/* eslint-disable import/no-cycle */
import React, { useRef, useMemo, useEffect } from 'react'

// eslint-disable-next-line import/order
import { formatDate, parseDate } from 'react-day-picker/moment'
import 'moment/locale/es'

import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'

import { ErrorContainer } from 'components/common'
import { InputSearchDate } from 'components/input'
import Message from 'components/message'

import { ids } from './ids'
import { DatePickerContainerColumn, StyledDatePickerInput } from './styled'
import DatePickerNavbar from '../date-picker-navbar'
import { DatePickerTitle } from '../styled'

const DatePicker = ({
  showDatePickerAbove,
  centeredTitle,
  hideTitleOnDesktop,
  showTitle,
  semiBold,
  label,
  value,
  flatStyle,
  fromError,
  name,
  index,
  disableIndex,
  light,
  placeholder,
  format,
  fromMonth,
  toMonth,
  onChange,
  onBlur,
  locale,
  dayPickerProps,
  disabled,
  id,
  captionElement,
  placeholderFontSize,
  inputContainerHeight,
  ...rest
}) => {
  const datePickerRef = useRef(null)
  const datePickerInputRef = useRef(null)
  const datePickerCalendarRef = useRef(null)
  const datePickerNavbarRef = useRef(false)
  const {
    i18n: { language }
  } = useTranslation()
  const inputId = useMemo(() => id || uuid(), [id])
  const dateId = useMemo(() => uuid(), [])

  useEffect(() => {
    datePickerInputRef.current.input.addEventListener('keydown', handleTabPress)
    document.addEventListener('mousedown', handleBlur)
    document.addEventListener('focusin', handleBlur)
  }, [datePickerInputRef.current])

  const getCustomOverlay = (id, { classNames, children, ...props }) => {
    // eslint-disable-next-line
    const { selectedDay, tabIndex, ...otherProps } = props

    return (
      <div className={classNames.overlayWrapper} id={id} tabIndex={-1} {...otherProps}>
        <div className={classNames.overlay}>{children}</div>
      </div>
    )
  }

  const handleTabPress = event => {
    if (event.key == 'Tab' && !event.shiftKey) {
      event.stopImmediatePropagation()
      datePickerCalendarRef.current.focus()
    }
  }

  const handleBlur = event => {
    if (
      !datePickerInputRef.current?.input?.contains(event.target) &&
      !datePickerCalendarRef.current?.dayPicker?.contains(event.target)
    ) {
      datePickerRef.current?.hideDayPicker()
    }
  }

  const CaptionElement = ({ date }) => {
    const monthName = date.toLocaleDateString(language, { month: 'long' }).toUpperCase()
    const year = date.toLocaleDateString(language, { year: 'numeric' }).toUpperCase()

    return (
      <div className="DayPicker-Caption" role="heading" aria-level="1">
        <div>{`${monthName} ${year}`}</div>
      </div>
    )
  }

  return (
    <DatePickerContainerColumn centered={centeredTitle} showDatePickerAbove={showDatePickerAbove}>
      {!hideTitleOnDesktop && (
        <DatePickerTitle showTitle={showTitle} semiBold={semiBold} forId={inputId}>
          {label}
        </DatePickerTitle>
      )}
      <StyledDatePickerInput
        value={value}
        component={InputSearchDate}
        inputProps={{
          ref: datePickerInputRef,
          index,
          name,
          flatStyle,
          light,
          dateError: fromError,
          disableIndex,
          disabled,
          id: inputId,
          'aria-controls': ids.dateId,
          placeholderFontSize,
          inputContainerHeight,
          onBlur: e => setTimeout(() => !datePickerNavbarRef.current && onBlur && onBlur(e), 50)
        }} /* Represents the attributes that the input element will have */
        overlayComponent={props => getCustomOverlay(dateId, props)}
        ref={datePickerRef}
        keepFocus={false}
        placeholder={placeholder}
        format={format}
        formatDate={formatDate}
        parseDate={parseDate}
        onDayChange={onChange}
        dayPickerProps={{
          ...rest,
          ref: datePickerCalendarRef,
          locale,
          fromMonth: fromMonth,
          toMonth: toMonth,
          captionElement: captionElement ? captionElement : <CaptionElement />,
          ...dayPickerProps,
          // eslint-disable-next-line react/display-name
          navbarElement: props => (
            <DatePickerNavbar
              {...props}
              fromMonth={fromMonth}
              toMonth={toMonth}
              datePickerNavbarRef={datePickerNavbarRef}
            />
          )
        }}
      />
      {fromError && (
        <ErrorContainer>
          <Message type="formInputError">{fromError}</Message>
        </ErrorContainer>
      )}
    </DatePickerContainerColumn>
  )
}

export default DatePicker
